<template>
  <div
    class="aha-presentation-editor-wrapper"
    :class="{
      'show-grid-slide': showingSlideGrid,
      'mobile-active': $store.state.activeModelDetailSlide,
    }"
  >
    <SocketError />
    <LoadingScreen v-if="!isRendering" />
    <SignUpCTAHeader v-if="isGuestUser" />

    <div
      class="z-10 h-14 md:h-15 aha-presentation-editor-header"
      :class="{'in-demo-mode': isGuestUser}"
      v-if="$store.state.presentation.id && isRendering"
    >
      <!--z-10 for header box shadow to appear -->
      <EditorHeader @update-online-count="updatePresentationSessionOnlineCount" />
    </div>
    <!-- 150 is higher than canvas's ahaslides logo (127) -->
    <public-announcement
      :z-index="150"
    />
    <div class="flex flex-1 min-h-0">
      <SideLeft v-if="!$store.state.sharePresentation" />
      <div
        class="relative flex min-h-0"
        :class="{'flex-1': !$store.state.indicators.showingSlideGrid}"
      >
        <!-- slide search popup cover -->
        <div
          v-if="!isEURegion && isShowingSlideSearchPopup"
          class="slide-search-container"
        >
          <SlideSearchPopup
            :closeSlideSearchPopup="closeSlideSearchPopup"
          />
        </div>

        <Middle
          @nextSlideHandler="nextSlideHandler"
          class="aha-middle"
          :class="{'editor-mode': !isBackstage, 'showing-popup-search': isShowingSlideSearchPopup}"
        />
        <SideRight v-if="showSideRight" class="aha-side-right" />
      </div>
    </div>

    <BeTakenOverModal
      v-if="isShowBeTakenOverModal"
      @hide="closeBeTakenOverModal()"
      :presenter="tookOverPresenter"
    />
    <PresenterWindowNotifyModal
      v-if="showPresenterWindowNotifyModal"
      @hide="showPresenterWindowNotifyModal = false"
    />
  </div>
</template>

<script>
import _ from 'lodash'
import screenfull from 'screenfull'

import * as Sentry from '@sentry/vue'
import { mapState, mapActions, mapGetters } from 'vuex'
import {
  SET_CURRENT_SLIDE_MODE,
  FULL_SCREEN_MODE,
  WINDOWED_MODE,
  EDIT_MODE,
  SET_CURRENT_ACTIVE_SLIDE,
} from '@/vuex/types'

import PresentationApi from '@/api/presentation'
import ImportPPTMixin from '@/mixins/presentation/ImportPPTMixin'

// components
import LoadingScreen from '@/components-v2/common/loading/LoadingScreen.vue'
import Middle from '@/components/presentations/Middle'
import BeTakenOverModal from '@/components-v2/presentation-editor/collaboration/BeTakenOverModal.vue'
import SlideSearchPopup from '@/components-v2/presentation-editor/new-slide/SlideSearchPopup.vue'
import SocketError from '@/components/UI/SocketError'
import socketHelper from '@/helpers/socket/socket-helper'
import PublicAnnouncement from '@/components-v2/common/global-notification/PublicAnnouncement.vue'
import PresentationDetailUtils from '@/utils/PresentationDetailUtils'

// configs
import Config from '@/config'

// utils
import Utils from '@/util'
import PresentationUtils from '@/utils/presentation'

// socket
import Socket from '@/socket'

// pricing
import Pricing from '@/pricing'

// event bus
import EventBus from '@/eventBus'
import mixpanelUtils from '@/utils/mixpanel.utils'

// services
import cookie from '@/services/cookie'
import { handleLoadAllImageCache } from '@/utils/indexedDB/image.cache.utils'
import { setObjectToIndexedDB, Stores, getObjectFromIndexedDB } from '@/utils/indexedDB/indexed.db'
// constants
import constants from '@/constant'
import SignUpCTAHeader from './demo-mode/SignUpCTAHeader.vue'
import EditorHeader from './editor-header/EditorHeader.vue'
import SideRight from './side-right/SideRight.vue'
import SideLeft from './side-left/SideLeft.vue'
import { DefaultCanvasBlocks, _migrateDefaultBlockIds } from '@/utils/CanvasBlocksLayout'
import PresenterWindowNotifyModal from './collaboration/PresenterWindowNotifyModal.vue'
import gaCategories from '@/constant/gaCategories'

const CREATE_REACTION = 'create-reaction'

export default {
  components: {
    SideLeft,
    EditorHeader,
    Middle,
    SideRight,
    LoadingScreen,
    SignUpCTAHeader,
    BeTakenOverModal,
    SocketError,
    PublicAnnouncement,
    PresenterWindowNotifyModal,
    SlideSearchPopup,
  },
  mixins: [ImportPPTMixin],
  data() {
    return {
      refer: null,
      activeTab: 1,
      isRendering: false,
      quizwordcloudDebound: null,
      isDebouncing: false,
      isDebouncingReaction: false,
      isDebouncingOnlineCount: false,
      isDebouncingGaOnlineCount: false,
      onlineCount: 0,
      showModalUploadImg: false,
      sockets: {},
      isShowBeTakenOverModal: false,
      tookOverPresenter: null,
      showPresenterWindowNotifyModal: false,
      isShowingSlideSearchPopup: false,
      arrowPos: 0,
    }
  },
  computed: {
    ...mapState({
      presentation: (state) => state.presentation,
      presentations: (state) => state.presentations,
      sharePresentation: (state) => state.sharePresentation,
      isNewPresentation: (state) => state.isNewPresentation,
      showingSlideGrid: (state) => state.indicators.showingSlideGrid,
      slideActive: (state) => state.slideActive,
      socketId: (state) => state.socketId,
      isGuestUser: (state) => state.currentUser.isGuest,
      currentUser: (state) => state.currentUser,
      userOnline: (state) => state.userOnline,
      onlineStatus: (state) => state.onlineStatus,
      plan: (state) => state.plan,
      isPresenterWhiteLabel: (state) => state.isPresenterWhiteLabel,
      workingMode: (state) => state.workingMode,
    }),
    ...mapGetters(['isPresenting', 'isBackstage']),
    isQuizLobbyScreen() {
      return this.slideActive.type === 'pickAnswer'
        && this.slideActive.quizStatus === constants.QuizStatuses.LOBBY_SCREEN
    },
    isQuizResultScreen() {
      return this.slideActive.type === 'pickAnswer'
        && this.slideActive.quizStatus === constants.QuizStatuses.RESULT_SCREEN
    },
    showSideRight() {
      return !this.$store.state.sharePresentation && this.currentUser.id && !this.isBackstage
    },
    isBlankPresentation() {
      const { isNewPresentation } = this.$store.state
      const { slides } = this.$store.state.presentation
      return isNewPresentation && slides.every((s) => !s.type)
    },
    isEURegion() {
      return Config.region === constants.euRegion
    },
    isTypeAnswerSlideType() {
      return this.slideActive.type === 'pickAnswer' && this.slideActive.slideType === 'typeAnswer'
    },
  },
  watch: {
    slideActive() {
      this._checkShowQuizCorrectAnswerIconReminder()
    },
  },
  created() {
    // prevent editor from scrolling
    document.body.classList.add('overflow-hidden')
    document.body.classList.add('fixed') // https://ahaslides.atlassian.net/browse/AHA-2696 -> fix simiar to using legacy's .no-scroll
    this.preloadListImagePreview()
    this.syncWorkingMode()
  },
  destroyed() {
    document.body.classList.remove('overflow-hidden')
    document.body.classList.remove('fixed') // https://ahaslides.atlassian.net/browse/AHA-2696 -> fix simiar to using legacy's .no-scroll
    document.body.classList.remove('presentation-detail-layout')

    socketHelper.leaveRoomPresentation(this.presentation.uniqueAccessCode)
    socketHelper.leaveRoomSlide(this.presentation.uniqueAccessCode, this.slideActive.id)

    Socket.getSocket().off('socket:online', this.handleSocketOnline)
    Socket.getSocket().off('socket:offline', this.handleSocketOffline)
    Socket.getSocket().off('presenter-takeover', this.handleTakeOverEvent)
    Socket.getSocket().off('create-idea', this.handleAudienceCreateIdeaEvent)
    Socket.getSocket().off('vote-idea', this.handleAudienceVoteIdeaEvent)
    Socket.getSocket().off('presenter-removed', this.handleRemovePresenterEvent)
    Socket.getSocket().io.off('reconnect', this.handleOnReconnect)
    Socket.getSocket().off('add-slides', this.handleAddSlides)
    Socket.getSocket().off('presentation:report_generated', this.handleUpdateReport)
    Socket.getSocket().off('backstage-control', this.handleBackstageControl)
    Socket.off(CREATE_REACTION, this.handleCreateReation)
    Socket.off('patch-presentation', this.handleCreateReation)

    Socket.unSubscribe(this.$store.state.presentation.uniqueAccessCode)
    Socket.unSubscribe(`${this.$store.state.presentation.uniqueAccessCode}-presenter`)

    EventBus.$off('onKeyboard')
    EventBus.$off('updateNextSlide')
    EventBus.$off('getDataPresentationDetail')
    EventBus.$off('slide-search-popup:open')
    window.removeEventListener('message', this.handleMessageTemplatePreview)
    document.removeEventListener('keydown', this.keyDown)
    this.$store.state.audiences = {}
    this.$store.state.userOnline = 0
    this.$store.state.questions = []
    this.$store.state.tabQnaQuestion = 1
    this.$store.state.lastQuestion = {}
    this.$store.state.questionCount = 0
    this.$store.state.showLiveAudienceDemo = false
    this.$store.state.showQnaSession = false
    this.$store.state.presentation.id = null
    this.$store.state.presentationEditors = []
    this.$store.state.isDisableEditPresentation = false
    Socket.unSubscribe(`user-${this.$store.state.currentUser.id}`)
    this.$store.state.openImportModal = false
    this.clearPresentationReportAction()
    this.$store.commit('unsetPresentation')
    this.clearPublicSlideTemplateAction()
  },
  async mounted() {
    window.addEventListener('message', this.handleMessageTemplatePreview, false)
    this.registerEventHistory()
    const { capture: isScreenCapture } = this.$route.query
    const { thumbnail: isCaptureContentSlideThumbnail } = this.$route.query

    if (this.$route.name === 'PresentationShare') {
      this.$store.state.sharePresentation = true
      if (isScreenCapture) {
        this.$store.state.isScreenCapture = true
      }
      if (isCaptureContentSlideThumbnail) {
        this.$store.state.isCaptureContentSlideThumbnail = true
      }
    } else {
      this.$store.state.sharePresentation = false
    }
    this.$store.state.isNewPresentation = Utils.getParamValue('new', window.location.href) === 'true'

    if (this.$store.state.isNewPresentation) {
      window.history.replaceState('', '', `/presentation/${this.$route.params.id}`)

      // after created new presentation then set flag = false
      this.$store.commit('setIsCreatingNewPresentation', false)
    }

    document.body.classList.add('presentation-detail-layout')
    if (!this.$store.state.currentUser.id && cookie.getValue('ahaToken')) {
      await this.getCurrentUserAction()
      this.handleImporting(this.$store.state.currentUser.id)
    } else if (this.$store.state.currentUser.id) {
      this.$store.state.plan = Pricing.getPlanByType(
        this.$store.state.currentUser.plan,
      )

      this.handleImporting(this.$store.state.currentUser.id)
    }

    document.onkeydown = (e) => {
      if (
        this.$store.state.slideActive.isFocusingInput &&
        Config.DISABLE_ON_FOCUS_SLIDE_KEYS.includes(e.code)
      ) {
        return
      }
      EventBus.$emit('onKeyboard', e)
    }

    // const dirty = true
    // window.onbeforeunload = function () {
    //   if (dirty) {
    //     return 'If you leave this page you will lose your unsaved changes.'
    //   }
    //   return null
    // }

    this.$store.state.isTouchScreen = Utils.checkTouchScreen()

    try {
      // FIXME: Consider removing this as there should be any reason
      // why we need a specific class for safari.
      // This was added by Loi to fix an issue with safari
      // but we lost track of the original issue.
      // @huongnguyen2152 15.05.2020
      // eslint-disable-next-line
      let isSafari = /constructor/i.test(window.HTMLElement) || (function (p) { return p.toString() === "[object SafariRemoteNotification]"; })(!window['safari'] || (typeof safari !== 'undefined' && safari.pushNotification));
      const ua = window.navigator.userAgent
      const iOS = !!ua.match(/iPad/i) || !!ua.match(/iPhone/i)
      const webkit = !!ua.match(/WebKit/i)
      const iOSSafari = iOS && webkit && !ua.match(/CriOS/i)
      if (isSafari || iOSSafari) {
        document.body.classList.add('safari-browser')
      }
    } catch (error) {
      Sentry.captureException(error)
    }

    Socket.getSocket().io.on('reconnect', this.handleOnReconnect)

    Socket.getSocket().on('socket:online', this.handleSocketOnline)
    Socket.getSocket().on('socket:offline', this.handleSocketOffline)
    Socket.getSocket().on('presenter-takeover', this.handleTakeOverEvent)
    Socket.getSocket().on('create-idea', this.handleAudienceCreateIdeaEvent)
    Socket.getSocket().on('vote-idea', this.handleAudienceVoteIdeaEvent)
    Socket.getSocket().on('presenter-removed', this.handleRemovePresenterEvent)
    Socket.getSocket().on('add-slides', this.handleAddSlides)
    Socket.getSocket().on('presentation:report_generated', this.handleUpdateReport)
    Socket.getSocket().on('backstage-control', this.handleBackstageControl)
    Socket.on(CREATE_REACTION, this.handleCreateReation)
    Socket.on('patch-presentation', this.handlePatchPresentation)

    EventBus.$on('updateNextSlide', _.debounce(this.handleUpdateNextSlide, 400))
    EventBus.$on('getDataPresentationDetail', () => {
      this.getDataPresentationDetail()
    })
    EventBus.$on('slide-search-popup:open', this.openSlideSearchPopup)

    // Now load
    await this.getDataPresentationDetail()
    this.initSocket(this.presentation.uniqueAccessCode)

    this.joinCollaborativeEditingRoom(this.presentation)

    if (this.isBackstage) {
      if (!JSON.parse(localStorage.getItem('PresenterWindowNotifyModal'))) {
        setTimeout(() => {
          this.showPresenterWindowNotifyModal = true
        }, 1000)
      }
    }
    if (Utils.isPageReloaded()) {
      socketHelper.backstageControl({
        uniqueAccessCode: this.presentation.uniqueAccessCode,
        type: 'reload',
      })
    }

    if (this._isPresentationEditorPage(this.$route)
      && this.currentUser
      && this.currentUser.id
      && !this.presentation.takenOverBy) {
      PresentationApi.takeOver(this.presentation.id)
    }

    // Support realtime in "share" view
    if (this.$route.name === 'PresentationShare') {
      // Using 'join-user' event for convenient :(
      Socket.getSocket().emit('join-user', `${this.$store.state.presentation.uniqueAccessCode}-presenter`)
    }
    // dipslay slide search by default if just created a blank prez
    if (this.isBlankPresentation && !this.isEURegion) {
      this.isShowingSlideSearchPopup = true
      this.patchPresentationAction({
        isShowingSlideSearch: true,
      })
    }
    this.handleTrackingReferPublicTemplate()
  },
  methods: {
    ...mapActions([
      'getCurrentUserAction',
      'patchPresentationAction',
      'setErrorOnInternalServerAction',
      'updateSlideActiveAction',
      'patchSlideByIdAction',
      'checkSlideMovingDirectionAction',
      'undoAction',
      'redoAction',
      'clearHistory',
    ]),
    ...mapActions('presentationReport', ['clearPresentationReportAction']),
    ...mapActions('presentationChat', ['audienceChangeStatusAction']),
    ...mapActions('contentSlideTemplates', ['clearPublicSlideTemplateAction']),
    registerEventHistory() {
      this.clearHistory()
      document.addEventListener('keydown', this.keyDown)
    },
    updatePresentationSessionOnlineCount: _.throttle((presentationId, onlineCount) => {
      PresentationApi.updateOnlineCountByPresentationId(presentationId, { onlineCount })
    }, 5000),
    handleTrackingReferPublicTemplate() {
      this.refer = Utils.getParamValue('refer', window.location.href)
      if (this.refer === constants.referPublicTemplate) {
        const query = { ...this.$route.query }
        delete query.refer
        this.$router.replace({ query })
        this.$store.state.isReferPublicTemplate = true
        Utils.gaTrackEvent(gaCategories.CATEGORY_PUBLIC_TEMPLATE_DEMO_MODE, 'Create new presentation', 'Use public template')
      }
    },
    keyDown(e) {
      const evtObj = window.event ? window.event : e
      if ((evtObj.ctrlKey || e.metaKey) && ((evtObj.keyCode === 90 && evtObj.shiftKey) || (evtObj.keyCode === 89))) {
        e.preventDefault()
        this.redoAction()
        return
      }
      if (evtObj.keyCode === 90 && (evtObj.ctrlKey || e.metaKey)) {
        e.preventDefault()
        this.undoAction()
      }
    },
    closeBeTakenOverModal() {
      EventBus.$emit('closePresentationPreview')
      this.isShowBeTakenOverModal = false
    },
    _checkIsDefaultSlideUserGroup(userCreatedAt) {
      const defaultSlideDeployDate = 1670346000000 // 07/12/2022 00:00
      return defaultSlideDeployDate < new Date(userCreatedAt).getTime()
    },
    handleMessageTemplatePreview(msg) {
      if (msg.data.type === constants.Events.EVENT_CHANGE_SLIDE) {
        const { presenterDomain } = Config
        let whiteLabelDomain = null
        if ((this.presentation && this.presentation.whiteLabelLink)
        || this.$store.state.presentation.whiteLabelLink) {
          if (this.presentation && this.presentation.whiteLabelLink) {
            whiteLabelDomain = this.presentation.whiteLabelLink
          } else {
            whiteLabelDomain = this.$store.state.presentation.whiteLabelLink
          }
        }
        if (whiteLabelDomain && `https://${whiteLabelDomain}` !== msg.origin) {
          return
        }
        if (presenterDomain !== msg.origin && Config.ssrDomain !== msg.origin) {
          return
        }
        const { id } = msg.data.slide
        EventBus.$emit('template-preview:go-to-slide', id)
      }
    },
    handleCreateReation: _.throttle(function (message) {
      const { audienceName, reactionType, ...partialPresentation } = message
      this.patchPresentationAction(partialPresentation)
      this._checkAndUpdateHasResults()
      EventBus.$emit(CREATE_REACTION, { audienceName, reactionType })
    }, 200),
    preloadListImagePreview() {
      const slideTypePreview = {}
      constants.allTypeSections.forEach((item) => {
        item.slideTypes.forEach((objType) => {
          if (objType.preview) {
            slideTypePreview[objType.preview] = {
              type: objType.preview,
              url: objType.previewUrl,
            }
          }
        })
      })

      const promiseAll = []
      Object.keys(slideTypePreview).forEach((key) => {
        promiseAll.push(Utils.preloadImage(slideTypePreview[key]))
      })
      Promise.all(promiseAll).then((results) => {
        results.forEach((item) => {
          slideTypePreview[item.type] = item
        })
        this.$store.state.slideTypePreview = slideTypePreview
      })
    },

    _isPresentationEditorPage(route) {
      return route.name === 'PresentationDetail' || route.name === 'PresentationBackstage'
    },

    handlePatchPresentation(payload) {
      // Reactions already set state, set again will results in flickering
      // https://ahaslides.atlassian.net/browse/AHA-7788
      if (payload.reactions) return
      this.patchPresentationAction(payload)
    },

    handleBackstageControl(payload) {
      const { socketId, type, data } = payload
      if (socketId === Socket.getSocketId()) return
      // Only update for other screens
      if (type === 'update-slide-active') {
        this.updateSlideActiveAction(data)
        if (data.quizStatus === 5 || data.showCorrectOption) {
          this._handleShowResultsOnQuizSlide()
        }
      } else if (type === 'start-quiz-on-other-screen') {
        EventBus.$emit('start-quiz-on-other-screen', data)
      } else if (type === 'reload') {
        // Backstage: Whenever reload quiz on origin screen
        // -> set quiz scene to Lobby on other screens
        if (this.slideActive.type === 'pickAnswer') {
          const updatedSlide = {
            quizStatus: constants.QuizStatuses.LOBBY_SCREEN,
          }
          this.updateSlideActiveAction(updatedSlide)
          updatedSlide.socketId = Socket.getSocketId()
          updatedSlide.accessCode = this.$store.state.presentation.uniqueAccessCode
          updatedSlide.presentationId = this.$store.state.presentation.id
          updatedSlide.presenting = this.$store.getters.isPresenting || this.isBackstage
          this.$http
            .put(Config.api.updateSlide + this.$store.state.slideActive.id, updatedSlide)
            .then(() => {
            }, () => {})
        }
      } else if (type === 'acceptTypeAnswer') {
        EventBus.$emit('accept-other-type-answers-from-backstage')
      }
    },

    joinCollaborativeEditingRoom(presentation) {
      // AHA-5534: Only join presenter collaborative room if active route
      // is the actual Presentation Editor (not the Share page, or the Presenting mode one)
      if (!presentation) return
      if (!presentation.id) return
      if (!presentation.uniqueAccessCode) {
        Sentry.captureMessage(`uniqueAccessCode not found. PresentationId: ${presentation.id}`, 'debug')
        return
      }
      if (
        this._isPresentationEditorPage(this.$route)
      ) {
        socketHelper.joinRoomPresentation(presentation.uniqueAccessCode)
        socketHelper.joinRoomSlide(presentation.uniqueAccessCode, presentation.activeSlide)
      }
    },

    async getDataPresentationDetail(options = { cachedContentSlides: true }) {
      let { id: presentationId } = this.$route.params
      const { beTakenOver } = this.$route.params
      const { embedded } = this.$route.query
      if (beTakenOver || embedded === 'true') {
        options.cachedContentSlides = false
      }

      let url = Config.api.getPresentationDetail
      if (this.$store.state.sharePresentation) {
        if (Utils.getShareCodeFromSlug(presentationId) !== null) {
          presentationId = Utils.getShareCodeFromSlug(presentationId)
        }
        url = Config.api.sharePresentation
      }
      const payloadDataPresentation = {
        socketId: Socket.getSocketId(),
      }

      if (this.$store.state.isAdmin) {
        payloadDataPresentation.admin = true
      }

      try {
        const res = await this.$http.get(url + presentationId, {
          params: payloadDataPresentation,
        })

        await this.handleDataPresentationDetail(res, {
          cachedContentSlides: options.cachedContentSlides,
        })

        const { presenting, preview: isPreviewPresentation } = this.$route.query

        if (isPreviewPresentation) {
          this.$store.state.isPreview = true
        }

        if (
          !presenting
            && presentationId
            && !isPreviewPresentation
            && !this.$store.state.isAdmin
            && this.presentation.presenting
            && this.currentUser.id == this.presentation.takenOverBy
        ) {
          if (
            this.$store.state.currentUser.rememberSettings
              && this.$store.state.currentUser.rememberSettings.fullscreen
              && !this.$store.state.isPlayDemoMode
          ) {
            try {
              screenfull.request()
              this.$store.dispatch(SET_CURRENT_SLIDE_MODE, FULL_SCREEN_MODE)
            } catch {
              this.$store.dispatch(SET_CURRENT_SLIDE_MODE, WINDOWED_MODE)
            }
          } else {
            this.$store.dispatch(SET_CURRENT_SLIDE_MODE, WINDOWED_MODE)
          }

          if (window.innerWidth > 800 && !this.$store.state.sharePresentation && !this.isBackstage) {
            this.$router.replace({
              name: 'PresentationDetail',
              params: { id: this.presentation.id },
              query: { presenting: true },
            })
          }
        }

        if (!this.$store.state.isAdmin && this.$store.state.slideActive.type == 'pickAnswer'
            && this.$store.state.slideActive.quizStatus != constants.QuizStatuses.LOBBY_SCREEN) {
          if (this.$store.state.sharePresentation) {
            return
          }
          const payload = {
            quizStatus: constants.QuizStatuses.LOBBY_SCREEN,
          }
          payload.socketId = Socket.getSocketId()
          payload.accessCode = this.$store.state.presentation.uniqueAccessCode
          payload.presentationId = this.$store.state.presentation.id
          payload.presenting = this.$store.getters.isPresenting || this.isBackstage
          this.$http
            .put(Config.api.updateSlide + this.$store.state.slideActive.id, payload)
            .then(() => {
            }, () => {})

          const indexSlide = Utils.getIndexSlide(
            this.$store.state.presentation.slides,
            this.$store.state.slideActive.id,
          )
          if (payload.quizStatus) {
            this.$store.state.presentation.slides[indexSlide].quizStatus =
                payload.quizStatus
          }
        }

        // handle when presenting need update on db
        if (
          presenting &&
            !this.presentation.presenting &&
            !this.$store.state.isAdmin &&
            !this.$store.state.sharePresentation &&
            !this.isBackstage
        ) {
          this._updatePresentingMode()
        }

        this._checkShowQuizCorrectAnswerIconReminder()
        EventBus.$emit('updateLastSlide')
      } catch (err) {
        console.log('presentation load error', err)
        this.$router.push('/apps/presentations')
        Utils.gaTrackEvent(constants.gaCategories.PRESENTATION_LIST, 'Presentation > Open > Failed')
        const matchPresentation =
            this.presentations.find((item) => presentationId === item.id)
        if (matchPresentation) {
          this.setErrorOnInternalServerAction(`Cannot open presentation ${`: ${matchPresentation.name}` || ''}`)
        } else {
          this.setErrorOnInternalServerAction('Presentation not found!')
        }
        Sentry.captureException(err)
      }
    },

    _handleShowResultsOnQuizSlide() {
      if (this.presentation.manualRevealCorrectAnswers && !this.slideActive.showCorrectOption) {
        return
      }
      if (this.isTypeAnswerSlideType) {
        EventBus.$emit('eventShowQuizTypeAnswer')
      }
      setTimeout(() => {
        this.updateSlideActiveAction({
          showCorrectOption: true,
          hideResult: false,
        })
      }, 2500)
    },

    _updatePresentingMode() {
      if (!this.$store.state.slideActive.id || !this.$store.state.presentation.id) return
      const payload = {
        presenting: true,
        socketId: Socket.getSocketId(),
        activeSlidePreseting: this.$store.state.slideActive.id,
        activeSlideAccessCode: this.$store.state.presentation.uniqueAccessCode,
      }
      this.$http
        .put(Config.api.updatePresentation + this.$store.state.presentation.id, payload)
        .then(() => {
        }, () => {})
      this.$store.state.presentation.presenting = true
    },

    _getAllSlidesNeedReminder(allQuizSlides) {
      return allQuizSlides.filter((slide) => {
        if (slide.slideType === 'typeAnswer') {
          this._updateSlideCorrectTypeAnswer(slide)
          if ((slide.correctQuizTypeAnswer && slide.correctQuizTypeAnswer.replace(/\s/g, '') !== '') || (slide.otherCorrectQuiz && slide.otherCorrectQuiz.length && slide.otherCorrectQuiz.some((option) => option.title && option.title.replace(/\s/g, '') !== ''))) {
            return false
          }
          return true
        }

        if (slide.SlideOptions && slide.SlideOptions.every((slideOption) => !slideOption.correct)) {
          return true
        }

        return false
      })
    },

    _checkAndUpdateHasResults() {
      if (!this.presentation.hasResults) {
        this._updatePresentationHasResults(true)
      }
    },

    _updateSlideCorrectTypeAnswer(slide) {
      const { slideActive } = this.$store.state
      if (slide.id == slideActive.id) {
        slide.correctQuizTypeAnswer = slideActive.correctQuizTypeAnswer
        slide.otherCorrectQuiz = slideActive.otherCorrectQuiz
      }
    },

    _setSlideIconReminder(type, allSlides) {
      allSlides.forEach((slide) => {
        if (type == 'has check') {
          slide.notCheckCorrectAnswer = false
        } else if (type == 'some not checked') {
          slide.notCheckCorrectAnswer = true
        }
      })
    },

    _checkShowQuizCorrectAnswerIconReminder() {
      const { slides } = this.$store.state.presentation
      this._setSlideIconReminder('has check', slides)
      const exceptSlideTypes = ['matchPairs', 'correctOrder']

      const allQuizSlides = slides.filter((slide) => slide.type === 'pickAnswer'
        && !exceptSlideTypes.includes(slide.slideType))

      const allSlidesNeedReminder = this._getAllSlidesNeedReminder(allQuizSlides)
      this.$store.state.presentation.quizNoCorrectAnswer = allSlidesNeedReminder

      this._setSlideIconReminder('some not checked', allSlidesNeedReminder)
    },

    async handleDataPresentationDetail(res, { cachedContentSlides = true }) {
      const _dispatch = this.$store.dispatch
      const noAudienceAdmissionFields = (res.body.audienceAdmission &&
        res.body.audienceAdmission.isAudienceAdmission &&
        !res.body.audienceAdmission.fields) || !res.body.audienceAdmission

      this.sockets[Socket.getSocketId()] =
        PresentationDetailUtils.setPresentationSockets(this.currentUser)

      res.body.slides = res.body.Slides

      this.patchPresentationAction({
        submitSlideLookup: res.body.submitSlideLookup || null,
      })

      await handleLoadAllImageCache(res.body.slides)

      this.$store.state.userOnline = res.body.userOnline

      this.$store.state.onlineStatus = PresentationDetailUtils.setOnlineStatus(this.currentUser,
        this.sharePresentation, this.userOnline, this.onlineStatus, this.plan)

      PresentationDetailUtils.setPresentationLanguage(res, _dispatch)

      PresentationDetailUtils.setBrowserTitle(res, this.isPresenterWhiteLabel, this.isBackstage)

      PresentationDetailUtils.sortAndUpdateSlides(res, _dispatch,
        this.isNewPresentation, this.slideActive, this.sharePresentation, this.$route.params.id)

      this.$store.dispatch('setPresentation', res.body)

      if (noAudienceAdmissionFields) {
        this._setDefaultFields(res.body)
        this.updatePresentation({
          audienceAdmission: this.presentation.audienceAdmission,
        })
      }

      PresentationDetailUtils.handleActiveTab(this.isNewPresentation,
        this.sharePresentation, _dispatch, res)

      if (!res.body.activeSlide && res.body.slides.length) {
        if (!res.body.slides[0].type) {
          this.$store.state.isNewPresentation = true
        }
      }

      PresentationDetailUtils.setPrezFontFamily(this.presentation.fontFamily)

      PresentationDetailUtils.getAudienceOnline(res, this.slideActive, this.presentation)

      this.handleCacheImage(res.body)
      this.getQuestions(res.body.id, res.body.slides)
      await this.loadContentSlidesCanvasBlocks(res.body.slides, { cachedContentSlides })
      setTimeout(() => {
        this.isRendering = true
      }, 500)
      PresentationDetailUtils.detectScreenAndSetTransition()
    },

    _setDefaultFields(detailPresentation) {
      const arr = []
      if (!detailPresentation.audienceAdmission) {
        detailPresentation.audienceAdmission = {}
      }
      if (detailPresentation.audienceAdmission && !detailPresentation.audienceAdmission.heading) {
        this.$store.state.presentation.audienceAdmission.heading = this.$t('welcomeToMyPresentation')
      }
      Object.values(constants.audienceAdmissionFields).forEach((item) => {
        this._setLabelToLanguage(item)
        arr.push(item)
      })
      this.$store.state.presentation.audienceAdmission.fields = arr
    },

    _setLabelToLanguage(option) {
      if (option.type === 'name') {
        option.label = this.$t('enterYourName')
      }
      if (option.type === 'team') {
        option.label = this.$t('enterYourOrganisation')
      }
      if (option.type === 'email') {
        option.label = this.$t('enterYourEmail')
      }
    },
    syncWorkingMode() {
      const currentMode = window.localStorage.getItem(constants.workspace.WORKING_MODE)
      if (currentMode && this.$store.state.workingMode !== currentMode) {
        this.$store.state.workingMode = currentMode
      }
    },
    async loadContentSlidesCanvasBlocks(slides, { cachedContentSlides = true }) {
      const contentSlides = slides.filter((item) => {
        return item && item.type === 'freestyle'
      })

      const getContentPromises = contentSlides.map(async (s) => {
        const cachedData = await getObjectFromIndexedDB(s.id, Stores.contentSlideCanvasBlocksStore)
        if (cachedContentSlides && cachedData && cachedData.canvasBlocks) {
          return {
            id: s.id,
            canvasBlocks: cachedData.canvasBlocks,
          }
        }

        if (s.canvasBlocksUrl) {
          const response = await this.$http.get(s.canvasBlocksUrl)
          let canvasBlocks = response.body ? response.body : DefaultCanvasBlocks(s.id)
          canvasBlocks = _migrateDefaultBlockIds(canvasBlocks, s.id)

          setObjectToIndexedDB(s.id, Stores.contentSlideCanvasBlocksStore, {
            canvasBlocks,
          })
          return {
            id: s.id,
            canvasBlocks,
          }
        }

        let canvasBlocks = DefaultCanvasBlocks(s.id)
        canvasBlocks = _migrateDefaultBlockIds(canvasBlocks, s.id)
        return {
          id: s.id,
          canvasBlocks,
        }
      })

      await Promise.all(getContentPromises).then((data) => {
        // Update canvasBlocksArray to all slides data in the presentation
        this.$store.state.presentation.slides = this.$store.state.presentation.slides.map((s) => {
          if (s.type !== 'freestyle') return s
          const slideIndex = data.findIndex((t) => t.id === s.id)
          const dataCanvasBlocks = data[slideIndex] ? data[slideIndex].canvasBlocks : null

          return {
            ...s,
            canvasBlocks: dataCanvasBlocks,
          }
        })
      })

      await this.updateCanvasBlockForSlideActive(contentSlides, { cachedContentSlides })
    },

    async updateCanvasBlockForSlideActive(contentSlides, { cachedContentSlides }) {
      const contentSlideActiveIndex = contentSlides.findIndex((item) => item.id === this.slideActive.id)
      if (contentSlideActiveIndex > -1) {
        try {
          const cachedData = await getObjectFromIndexedDB(
            this.slideActive.id,
            Stores.contentSlideCanvasBlocksStore,
          )
          if (cachedContentSlides && cachedData && cachedData.canvasBlocks) {
            this.$store.dispatch('updateSlideActive', {
              canvasBlocks: cachedData.canvasBlocks,
            })
          } else {
            const resGetContentSlideActive = await this.$http.get(contentSlides[contentSlideActiveIndex].canvasBlocksUrl)
            let dataCanvasBlocks = resGetContentSlideActive.body ?
              resGetContentSlideActive.body :
              DefaultCanvasBlocks(this.slideActive.id)
            dataCanvasBlocks = _migrateDefaultBlockIds(dataCanvasBlocks, this.slideActive.id)

            this.$store.dispatch('updateSlideActive', {
              canvasBlocks: dataCanvasBlocks,
            })
          }
        } catch (e) {
          console.log(e)
        }
      }
    },

    async handleTakeOverEvent(doc) {
      if (this.currentUser.id === this.presentation.takenOverBy &&
        doc.takenOverBy !== this.currentUser.id) {
        Object.keys(this.sockets).forEach((k) => {
          const socket = this.sockets[k]

          if (doc.takenOverBy === socket.id) {
            this.tookOverPresenter = socket
            this.presentation.TakenOverByUser = socket
          }
        })

        EventBus.$emit('clearOtherPopups')
        setTimeout(() => {
          this.isShowBeTakenOverModal = true
        }, 300)
      }

      this.presentation.takenOverBy = doc.takenOverBy

      this.refreshEditor()

      // AHA-5372 Presenting > Khi đang presenting và bị take over, presenter đó chưa được chuyển về màn hình Editor
      if (doc.takenOverBy != this.currentUser.id) {
        this.$store.dispatch(SET_CURRENT_SLIDE_MODE, EDIT_MODE)
        this.$router.replace({
          name: 'PresentationDetail',
          params: {
            id: this.$store.state.presentation.id,
            beTakenOver: true,
          },
        })
      } else {
        await this.getDataPresentationDetail({ cachedContentSlides: false })

        // This is a hack. We should not do this.
        const currentSlideId = this.$store.state.slideActive.id
        if (currentSlideId) {
          this.$store.dispatch('updateSlideActive', {
            activeTab: null,
            id: null,
          })
          setTimeout(() => {
            this.$store.dispatch(SET_CURRENT_ACTIVE_SLIDE, currentSlideId)
            EventBus.$emit('eventHandleSetValueTransition')
          }, 0)
        }
      }
    },

    async _updatePresentationHasResults(hasResults) {
      const payload = {
        hasResults,
      }
      PresentationUtils.updatePresentationHasResults(this.presentation, hasResults)
      setTimeout(() => {
        this.patchPresentationAction(payload)
      }, 500)
    },

    handleRemovePresenterEvent(doc) {
      const { removedPresenters } = doc
      if (removedPresenters.indexOf(this.currentUser.email) > -1) {
        Utils.showToastError('You have been removed from this presentation')
        window.setTimeout(() => {
          window.location.reload()
        }, 2000)
      }
    },
    handleAddSlides(payload) {
      const slideTypes = {}
      constants.allTypeSections.forEach((item) => {
        item.slideTypes.forEach((typeObj) => {
          slideTypes[typeObj.type] = typeObj
        })
      })

      payload.slides.forEach((item) => {
        if (item.type) {
          item.typeObj = slideTypes[item.type]
        }
      })

      this.$store.state.presentation.slides = this.$store.state.presentation.slides.concat(
        payload.slides,
      )
    },

    handleUpdateReport(payload) {
      this.patchPresentationAction({
        exportData: {
          ...this.presentation.exportData,
          ...payload,
        },
      })
    },

    refreshEditor() {
      const presenters = []
      Object.keys(this.sockets).forEach((k) => {
        const socket = this.sockets[k]
        const idx = presenters.findIndex((p) => { return p.id === socket.id })
        if (idx === -1) {
          socket.isMyData = socket.id == this.currentUser.id
          if (socket.id == this.currentUser.id && this.isGuestUser) {
            // Demo mode, show you as Guest
            socket.name = 'Guest'
          }
          presenters.push(socket)
        }
      })

      presenters.sort((a, b) => {
        if (this.presentation.takenOverBy === a.id) return -1
        if (this.presentation.takenOverBy === b.id) return 1
        if (a.id < b.id) return -1
        if (a.id > b.id) return 1
        return 0
      })

      this.$store.state.presentationEditors = presenters

      if (this.presentation.takenOverBy) {
        this.$store.state.isDisableEditPresentation =
          this.presentation.takenOverBy && this.currentUser.id !== this.presentation.takenOverBy
      }
    },

    handleSocketOnline(doc) {
      if (!this.sockets[doc.socketId] || !this.sockets[doc.socketId].id) {
        this.sockets[doc.socketId] = doc.presenter
        this.joinCollaborativeEditingRoom(this.presentation)
      }
      this.refreshEditor()
    },

    handleSocketOffline(doc) {
      // Skip on shared screen
      if (this.$route.name === 'PresentationShare') return

      delete this.sockets[doc.socketId]
      this.refreshEditor()
    },

    initSocket(channel) {
      Socket.subscribe(`${channel}-presenter`, (doc) => {
        if (doc.socketId == Socket.getSocketId()) {
          return
        }

        if (doc.t === 'account-removal') {
          window.location.href = Config.accountRemovalURL
          return
        }

        if (doc.type == 'uO') {
          this._handleUserOnlineCount(doc)
          return
        }

        let index
        const { presentation } = this.$store.state

        // handle socket msg answer quiz, poll from audience
        if (doc.t == 'a') {
          this._checkAndUpdateHasResults()
          let indexSlideVote = null

          if (doc.s != this.$store.state.slideActive.id) {
            const indexSlideInPresentation = this.$store.state.presentation.slides.findIndex((item) => {
              return item.id == doc.s
            })

            if (indexSlideInPresentation !== -1) {
              indexSlideVote = indexSlideInPresentation
            }
          }

          if (doc.iTa) {
            if (!this.$store.state.slideActive.multipleCountAnswer) {
              this.updateSlideActiveAction({
                multipleCountAnswer: 0,
              })
              this.$store.state.tempAudienceCount = 0
            }

            if (indexSlideVote === null) {
              this.$store.state.tempAudienceCount++
              this.updateVoteCountThrottle(doc, null)
            } else {
              this.$store.state.presentation.slides[indexSlideVote].voteCache++
              this.updateVoteCountThrottle(doc, indexSlideVote)
            }

            if (!this.$store.state.sharePresentation && this.$store.state.slideActive.type == 'pickAnswer') {
              if (this.$store.state.userOnline < 30) {
                if (this.$store.state.audiences[doc.a]) {
                  this.$store.state.audiences[doc.a][doc.s] = true
                  this._checkEveryoneVotedQuiz()
                }
              }
            }
            return
          }

          if (doc.v && doc.v.length) {
            if (!this.$store.state.slideActive.multipleCountAnswer) {
              this.updateSlideActiveAction({
                multipleCountAnswer: 0,
              })
              this.$store.state.tempAudienceCount = 0
            }

            if (indexSlideVote === null) {
              this.$store.state.tempAudienceCount++
              this.updateVoteCountThrottle(doc, null)
            } else {
              this.$store.state.presentation.slides[indexSlideVote].voteCache++

              this.updateVoteCountThrottle(doc, indexSlideVote)
            }

            this._updateSlideOptions(doc)
            if (this.$store.state.presentation.audiencePacing) {
              // update chart whenever audience answers
              this.$store.dispatch('updateSlideActive', this.$store.state.slideActive)
            }

            if (!this.$store.state.sharePresentation && this.$store.state.slideActive.type == 'pickAnswer') {
              if (this.$store.state.userOnline < 30) {
                if (this.$store.state.audiences[doc.a]) {
                  this.$store.state.audiences[doc.a][doc.s] = true
                  this._checkEveryoneVotedQuiz()
                }
              }
            }
          }

          return
        }

        if (doc.type == 'question-and-answer') {
          if (doc.action == 'create') {
            this._checkAndUpdateHasResults()
            if (!this.presentation.isModerationMode) {
              const arr = JSON.parse(JSON.stringify(this.$store.state.questions))
              arr.push(doc.data)
              Utils.updateDataQuestionAnswer(arr, this.$store.state.tabQnaQuestion)
              EventBus.$emit('questionAnimationAction')
            } else {
              this.$store.state.questions.push(doc.data)
            }
            Utils.gaTrackEvent('Question And Answer', 'Question Received', 'Question Received')
          } else if (doc.action == 'vote' || doc.action === 'question-updated') {
            this._updateQuestionAnswer(doc.data)
          }
        }

        // using both channel
        if (doc.type == 'slide' && doc.data.id == this.$store.state.slideActive.id) {
          const { data } = doc

          const payload = {
            type: doc.type,
            layout: doc.layout,
            multipleChoice: data.multipleChoice,
            limitChoice: data.limitChoice,
            typeChart: data.typeChart || 'barChart',
            hideResult: data.hideResult,
            addCorrectOption: data.addCorrectOption,
            stopSubmission: data.stopSubmission,
            showAllBulletPoints: data.showAllBulletPoints,
            image: data.image,
            imageCaption: data.imageCaption,
            youtubeLink: data.youtubeLink,
            backgroundImage: data.backgroundImage,
            textColour: data.textColour,
            baseColour: data.baseColour,
            visibility: data.visibility,
            showQuestionImage: data.showQuestionImage,
          }

          if (!this.$store.state.sharePresentation) {
            payload.showCorrectOption = doc.data.showCorrectOption
          }

          this.updateSlideActive(payload)
        }

        if (doc.type == 'presentation') {
          if (
            doc.action == 'nextSlide' &&
            !this.$store.state.sharePresentation &&
            !this.$store.state.isDisableEditPresentation
          ) {
            if (doc.data.id == this.$store.state.slideActive.id) return
            const index = this.$store.state.presentation.slides.findIndex(
              (item) => {
                return item.id == doc.data.id
              },
            )
            this.$store.dispatch('checkSlideMovingDirectionAction', doc.data.id)
            this.$store.dispatch('updateSlideActive', {
              activeTab: null,
              id: null,
            })
            setTimeout(() => {
              this.$store.dispatch(
                'setSlideActive',
                this.$store.state.presentation.slides[index],
              )

              EventBus.$emit('eventHandleSetValueTransition')
            }, 0)
          }

          if (doc.action == 'uniqueAccessCode') {
            this.$store.state.presentation.qrcode = doc.data.qrcode
            this.$store.state.presentation.uniqueAccessCode =
              doc.data.uniqueAccessCode
            this.$store.state.presentation.shareCode = doc.data.shareCode
          }

          if (doc.action == 'changeAccessCode') {
            this.$store.state.presentation.accessCode = doc.data.accessCode
          }

          if (doc.action == 'presenting' && !this.$store.state.sharePresentation) {
            if (this.$store.state.isPreview || doc.data.presentFromPPT) return

            this.$store.state.presentation.presenting = doc.data.presentation.presenting

            // sửa thành tất cả các tabs present cùng nhau
            // để có thể khắc phục tình trạng 1 máy đang present
            // nhưng 1 tab khác edit thì màn hình đang present sẽ không được update
            // theo và nhảy lung tung mà don't know why.
            if (!this.$store.state.isDisableEditPresentation) {
              if (doc.data.presentation.presenting) {
                this.$store.dispatch(SET_CURRENT_SLIDE_MODE, WINDOWED_MODE)
              } else {
                this.$store.dispatch(SET_CURRENT_SLIDE_MODE, EDIT_MODE)
              }
            }
          }

          if (doc.action == 'privateMode') {
            this.$store.state.presentation.privateMode = doc.data.presentation.privateMode
          }
        }

        if (doc.type === 'user-online-count') {
          if (doc.data || doc.data === 0) {
            if (doc.data > this.$store.state.userOnline) {
              EventBus.$emit('userOnlineCount')
              if (this.isPresenting && !this.sharePresentation) {
                this.updatePresentationSessionOnlineCount(this.$store.state.presentation.id, doc.data)
              }
            }

            this.onlineCount = doc.data
            this._handleOnlineCountThrottled()
            if (!this.maxOnlineCount || this.onlineCount > this.maxOnlineCount) {
              this.maxOnlineCount = this.onlineCount
            }
          }

          if (doc.off) {
            if (this.$store.state.audiences[doc.off]) {
              this.$store.state.audiences[doc.off].offline = true
              const offlineAudience = this.$store.state.audiences[doc.off]
              if (this.$store.state.presentation.teamPlay) {
                this._getAndSetPayloadAudienceTeamName(offlineAudience)
              }
              this.audienceChangeStatusAction(offlineAudience)
            }

            if (this.$store.state.userOnline < 60 && this.$store.state.slideActive.type == 'pickAnswer'
              && this.$store.state.slideActive.quizStatus == constants.QuizStatuses.LOBBY_SCREEN) {
              this._getAudienceOnlineJoinQuiz()
            }
          }
        }

        if (doc.type == 'slideOption' && doc.action == 'audience-create') {
          this._checkAndUpdateHasResults()
          if (this.slideActive.type === 'openEnded') {
            if (this.slideActive.id === doc.data.slideId) {
              EventBus.$emit('new-opended', doc.data)
            }

            return
          }

          const matchSlide = this.presentation.slides.find((slide) => slide.id === doc.data.slideId)
          if (!matchSlide) return

          const newSlideOptions = [...matchSlide.SlideOptions, doc.data]
          this.patchSlideByIdAction({
            id: doc.data.slideId,
            SlideOptions: newSlideOptions,
          })
        }

        if (
          doc.type == 'slideWordCloud' &&
          doc.action == 'audience-wordcloud'
        ) {
          this._checkAndUpdateHasResults()
          if (doc.data.slideId == this.slideActive.id) {
            const newSlideOptions = this.slideActive.SlideOptions.concat(doc.data.options)
            this.updateSlideActiveAction({
              SlideOptions: newSlideOptions,
            })

            EventBus.$emit('audienceWordCloud')
          } else {
            index = Utils.getIndexSlide(presentation.slides, doc.data.slideId)
            if (index != -1) {
              presentation.slides[index].SlideOptions = presentation.slides[
                index
              ].SlideOptions.concat(doc.data.options)
            }
          }
        }

        if (doc.type == 'active-one-time') {
          this.$store.state.currentUser.oneTimeEnd = doc.data
          return
        }

        if (doc.type == 'export-status') {
          this.$store.dispatch('updatePresentation', { exportData: doc.exportData })
          return
        }

        if (doc.type === 'quiz-show-answer' && this.$store.state.sharePresentation) {
          if (this.$store.state.slideActive.id === parseInt(doc.data.slideId, 10)) {
            EventBus.$emit('eventShowQuizTypeAnswer')
          }
          return
        }

        if (doc.type === 'aLQT') {
          this._handleChangeQuizTeam(doc.data)
          EventBus.$emit('gamelobby-leave-team', doc.data)
          this._setTeamData()
        }

        if (doc.t === 'sca') {
          this._checkAndUpdateHasResults()
          if (!this.$store.state.slideActive.multipleCountAnswer) {
            this.updateSlideActiveAction({
              multipleCountAnswer: 0,
            })
            this.$store.state.tempAudienceCount = 0
          }
          this.$store.state.tempAudienceCount++
          this._handleAudienceScaleQuestionThrottled()
          this.updateVoteCountForScaleThrottle()
        }

        if (doc.type === 'opentok') {
          if (doc.action === 'stop-call') {
            if (this.$store.state.voiceCall.audienceUnique === doc.data.audienceUnique) {
              EventBus.$emit('stop-video-call')
            }
          }
        }
      })
    },

    _updateQuestionAnswer(question) {
      const index = this.$store.state.questions.findIndex((item) => {
        return item.id == question.id
      })
      if (index === -1) {
        this.$store.state.questions.push(question)
      } else {
        this.$store.state.questions[index] = question
      }
      const arr = JSON.parse(JSON.stringify(this.$store.state.questions))
      Utils.updateDataQuestionAnswer(arr, this.$store.state.tabQnaQuestion)
    },

    _setTeamData: _.debounce(function () {
      if (this.$store.state.sharePresentation) return
      const { teamSize } = this.$store.state.presentation
      const teamCount = this._getTeamCount()
      const teamData = {}
      Object.keys(teamCount)
        .forEach((id) => {
          teamData[id] = {
            teamCount: teamCount[id],
            full: teamCount[id] >= teamSize,
          }
        })
      this.$http.post(Config.api.checkQuizTeamMember, {
        teamData,
        accessCode: this.$store.state.presentation.uniqueAccessCode,
      })
        .then(() => {}, () => {})
    }, 500),

    _handleChangeQuizTeam(data) {
      if (this.$store.state.audiences[data.a]) {
        delete this.$store.state.audiences[data.a]
      }
    },

    _getTeamCount() {
      const teams = {}

      Object
        .keys(this.$store.state.audiences)
        .forEach((key) => {
          const teamId = this.$store.state.audiences[key].audienceTeam

          teams[teamId] = teams[teamId] || 0
          teams[teamId] += 1
        })

      return teams
    },

    _getAndSetPayloadAudienceTeamName(audience) {
      this._setTeamData()
      const { teamData } = this.$store.state.presentation
      const audienceTeam = teamData.find((team) => team.id === audience.audienceTeam)
      if (audienceTeam) audience.audienceTeamName = audienceTeam.name
    },

    _updateSlideOptions(doc) {
      const { s: slideId, v: votedSlideOptionIds } = doc

      const matchSlide = this.presentation.slides.find((slide) => slide.id === slideId)
      if (!matchSlide) return

      const newSlideOptions = matchSlide.SlideOptions.map((slideOption) => {
        const hasVoted = votedSlideOptionIds.some((item) => item === slideOption.id)

        if (hasVoted) {
          const currentCount = slideOption.count || 0
          return {
            ...slideOption,
            count: currentCount + 1,
          }
        }

        return slideOption
      })

      this.patchSlideByIdAction({
        id: slideId,
        SlideOptions: newSlideOptions,
      })
    },

    _handleEventAudienceOnOff: _.throttle(function () {
      if (this.slideActive.type === 'spinnerWheel') {
        EventBus.$emit('updateEntries')
      }
    }, 1000),

    _handleOnlineCountThrottled: _.throttle(function () {
      this.$store.state.userOnline = this.onlineCount

      if (this.$store.state.currentUser.id) {
        if (!this.$store.state.sharePresentation) {
          const percentLimit =
            (this.$store.state.userOnline / this.$store.state.plan.limit) *
            100
          if (percentLimit > 75 && percentLimit < 95) {
            this.$store.state.onlineStatus = 2
          }
          if (percentLimit >= 95) {
            this.$store.state.onlineStatus = 3
          }
          if (percentLimit <= 75 && this.$store.state.onlineStatus != 1) {
            this.$store.state.onlineStatus = 1
          }
        } else {
          this.$store.state.onlineStatus = 1
        }

        mixpanelUtils.trackHostingEvent({
          presentation: this.presentation,
          onlineCount: this.onlineCount,
          maxOnlineCount: this.maxOnlineCount,
        })
      }
    }, 1000),

    updateVoteCountThrottle: _.throttle(function handleVoteCount(doc, index) {
      const { s: slideId } = doc
      const payload = {
        id: slideId,
        multipleCountAnswer: this.$store.state.tempAudienceCount,
      }

      if (index !== null) {
        payload.multipleCountAnswer = this.presentation.slides[index].voteCache
      }

      this.patchSlideByIdAction(payload)
    }, 250),

    _handleAudienceScaleQuestionThrottled: _.throttle(() => {
      EventBus.$emit('scaleQuestionAnswer')
    }, 1000),

    updateVoteCountForScaleThrottle: _.throttle(function updateVoteCountForScale() {
      this.updateSlideActiveAction({
        multipleCountAnswer: this.$store.state.tempAudienceCount,
      })
    }, 250),

    _handleUserOnlineCount(socketData) {
      const doc = {
        data: {
          audience: socketData.data.a,
          audienceName: socketData.data.n,
          audienceEmoji: socketData.data.e,
          quizOnline: socketData.data.q,
          audienceTeam: socketData.data.t,
        },
      }
      if (socketData.i) {
        doc.data.data = {
          id: socketData.data.i,
          answer: socketData.data.r,
        }
      }

      const params = {
        audience: doc.data.audience,
        audienceEmoji: doc.data.audienceEmoji,
        audienceName: doc.data.audienceName,
        offline: doc.data.offline,
        audienceTeam: doc.data.audienceTeam,
      }

      // start cleart
      const objAudience = this.$store.state.audiences[params.audience]
      if (!objAudience) {
        this.$store.state.audiences[params.audience] = params
      } else {
        if (objAudience.audienceName !== params.audienceName) {
          this.$store.state.audiences[params.audience].audienceName = params.audienceName
        }
        if (objAudience.audienceEmoji !== params.audienceEmoji) {
          this.$store.state.audiences[params.audience].audienceEmoji = params.audienceEmoji
        }
        if (objAudience.audienceTeam !== params.audienceTeam) {
          this.$store.state.audiences[params.audience].audienceTeam = params.audienceTeam
        }
        if (objAudience.offline !== params.offline) {
          this.$store.state.audiences[params.audience].offline = params.offline
        }

        if (doc.data.data) {
          if (
            this.$store.state.audiences[params.audience][doc.data.data.id] !== true
          ) {
            this.$store.state.audiences[params.audience][doc.data.data.id] = doc.data.data.answer
          }
        }
      }

      if (this.isQuizLobbyScreen || this.isQuizResultScreen || this.slideActive.type === 'qrcode') {
        this._getAudienceOnlineJoinQuiz()
        if (this.$store.state.presentation.teamPlay) {
          this._getAndSetPayloadAudienceTeamName(params)
        }
        this.audienceChangeStatusAction(params)
      }
      if (this.$store.state.slideActive.type === 'spinnerWheel' && this.$store.state.slideActive.wheelStatus !== 'spinning') {
        this._handleEventAudienceOnOff()
      }
    },

    _getAudienceOnlineJoinQuiz: _.throttle(function () {
      let count = 0
      Object.keys(this.$store.state.audiences).forEach((key) => {
        if (!this.$store.state.audiences[key].offline) {
          count++
        }
      })
      this.$store.state.countPlayer = count
      this._getGameLobbyQuiz()
    }, 250),

    _getGameLobbyQuiz: _.throttle(function (doc) {
      if (!this.$store.state.presentation.teamPlay) {
        EventBus.$emit('quiz-bus', doc)
      } else {
        EventBus.$emit('gamelobby-quiz-team', doc)
      }
    }, 1000),

    _checkEveryoneVotedQuiz() {
      if (this.$store.state.slideActive.type !== 'pickAnswer') {
        return
      }
      if (this.$store.state.slideActive.quizStatus !== constants.QuizStatuses.QUESTION_SCREEN) {
        return
      }

      let someoneHaveNotVote = false

      Object.keys(this.$store.state.audiences).forEach((key) => {
        const audience = this.$store.state.audiences[key]
        const audienceNotAnswer = !audience[this.$store.state.slideActive.id]
        if (audienceNotAnswer) {
          someoneHaveNotVote = true
        }
      })
      if (!someoneHaveNotVote) {
        EventBus.$emit('everyone-voted')
      }
    },

    toggleDropdownProfile() {
      this.dropdownProfile = !this.dropdownProfile
    },

    toggleMiniNavbar() {
      if (document.body.classList.contains('mini-navbar')) {
        document.body.classList.remove('mini-navbar')
      } else {
        document.body.classList.add('mini-navbar')
      }
    },

    updatePresentation(payload) {
      if (this.$store.state.sharePresentation) return
      this.$http
        .put(Config.api.updatePresentation + this.$route.params.id, payload)
    },

    nextSlideHandler(currentIndex) {
      const nextSlide = this.$store.state.presentation.slides[currentIndex + 1]
      if (nextSlide && this.$store.state.sharePresentation) {
        const arr = []
        this.buildImagesList(nextSlide, arr)
        this.preloadImages(arr)
      }
    },

    handleCacheImage(doc) {
      const arr = []
      if (doc && doc.slides) {
        if (doc.logo) {
          arr.push(doc.logo.thumbnail)
          arr.push(doc.logo.large)
        }

        // If not in sharing, preload all images, else preload only 2 slides
        const preload = !this.$store.state.sharePresentation ? doc.slides.length : 2
        doc.slides.slice(0, preload).forEach((slide) => this.buildImagesList(slide, arr))
      }

      this.preloadImages(arr)
    },

    buildImagesList(slide, arr) {
      if (slide.image) {
        arr.push(slide.image.thumbnail)
        arr.push(slide.image.xLarge)
      }

      if (slide.backgroundImage) {
        arr.push(slide.backgroundImage.thumbnail)
        arr.push(slide.backgroundImage.xLarge)
      }

      slide.SlideOptions.forEach((option) => {
        if (option.image) {
          arr.push(option.imageCropped)
        }
      })
    },

    preloadImages(array) {
      if (!this.preloadImages.list) {
        this.preloadImages.list = []
      }
      const { list } = this.preloadImages
      for (let i = 0; i < array.length; i++) {
        const img = new Image()
        img.onload = (() => {
          const index = list.indexOf(this)
          if (index !== -1) {
            // remove image from the array once it's loaded
            // for memory consumption reasons
            list.splice(index, 1)
          }
        })
        list.push(img)
        img.src = array[i]
      }
    },

    getQuestions(id, allSlides) {
      this.$http
        .get(Config.api.getQuestion, {
          params: {
            id,
          },
        })
        .then(
          (res) => {
            Utils.updateDataQuestionAnswer(
              res.body,
              this.$store.state.tabQnaQuestion,
            )
            const hasResults = PresentationUtils.checkPresentationHasResults(allSlides)
            const needUpdateHasResults = this.presentation.hasResults !== hasResults
            if (needUpdateHasResults) {
              this._updatePresentationHasResults(PresentationUtils.checkPresentationHasResults(allSlides))
            }
          },
          () => {},
        ).catch((err) => {
          this.$router.push('/apps/presentations')
          this.setErrorOnInternalServerAction('Cannot open this presentation')
          throw err
        })
    },

    getQuizIndex(id) {
      if (!id) return null
      const quizs = this.$store.state.presentation.slides.filter((item) => {
        return item.type == 'pickAnswer'
      })
      const index = Utils.getIndexSlide(quizs, id)
      return {
        index: index + 1,
        totalQuiz: quizs.length,
      }
    },

    async handleOnReconnect() {
      this.sockets = {}
      await this.getDataPresentationDetail()
      this.joinCollaborativeEditingRoom(this.presentation)
    },

    async handleUpdateNextSlide(idSlide) {
      if (this.$store.state.isDisableEditPresentation) {
        return
      }

      const presentationId =
        this.$store.state.presentation.id || this.$route.params.id
      if (!idSlide || !presentationId || this.$store.state.sharePresentation) {
        return
      }

      const indexSlide = this.$store.state.presentation.slides.findIndex((item) => {
        return item.id === idSlide
      })

      this.$store.dispatch('setSyncPresentation', 1)
      const payloadSlide = {
        activeSlide: idSlide,
        socketId: Socket.getSocketId(),
        accessCode: this.$store.state.presentation.uniqueAccessCode,
        timestamp: new Date().getTime(),
      }

      if (this.$store.state.slideActive.quizStatus !== -1) {
        payloadSlide.quizStatus = constants.QuizStatuses.LOBBY_SCREEN
      }

      let objQuiz = null
      if (this.$store.state.presentation.slides[indexSlide].type === 'pickAnswer') {
        objQuiz = this.getQuizIndex(idSlide)

        if (objQuiz) {
          payloadSlide.questionCount = objQuiz.totalQuiz
          payloadSlide.questionIndex = objQuiz.index
        }

        if (this.$store.state.continueQuiz) {
          payloadSlide.quizStatus = constants.QuizStatuses.RULE_SCREEN
        }
      }
      this.$store.state.swapLeaderboard = false
      if (this.$store.state.presentation.slides[indexSlide].type === 'leaderboard') {
        this.$store.dispatch('setSyncPresentation', 2)
        return
      }
      try {
        this.$http.put(Config.api.updateNextSlide + presentationId, payloadSlide)
        this.checkSlideMovingDirectionAction(this.slideActive.id)
        EventBus.$emit('enableQuizStart', idSlide)
        this.$store.dispatch('setSyncPresentation', 2)
      } catch (e) {
        this.$store.dispatch('setSyncPresentation', 3)
        Sentry.captureException(e)
      }
    },

    handleAudienceCreateIdeaEvent(doc) {
      this._checkAndUpdateHasResults()
      const matchSlide = this.presentation.slides.find((slide) => slide.id === doc.slideId)

      if (!matchSlide
        || !matchSlide.SlideOptions
        || matchSlide.SlideOptions.length >= constants.MAX_IDEAS_NUMBER) {
        return
      }

      let newSlideOptions = [...this.slideActive.SlideOptions, doc]
      newSlideOptions = newSlideOptions
        .map((idea, index) => ({ ...idea, createdAtOrder: index + 1 }))

      this.patchSlideByIdAction({
        id: doc.slideId,
        SlideOptions: newSlideOptions,
      })

      if (doc.slideId === this.slideActive.id) {
        EventBus.$emit('handleSortIdeasByCreatedAt')
      }

      EventBus.$emit('handleScrollingAndShowScroll')
    },

    handleAudienceVoteIdeaEvent: _.throttle(function throttleAudienceVoteIdea(doc) {
      const index = this.slideActive.SlideOptions.findIndex((option) => {
        return option.id === doc.slideOptionId
      })
      if (index === -1) return
      this.slideActive.SlideOptions[index].votesCount = doc.numberOfVotes
      this.updateSlideActiveAction(this.slideActive)
    }, 300),

    openSlideSearchPopup(pos) {
      this.arrowPos = pos
      this.isShowingSlideSearchPopup = true
      this.patchPresentationAction({
        isShowingSlideSearch: true,
      })
    },
    closeSlideSearchPopup() {
      this.isShowingSlideSearchPopup = false
      this.patchPresentationAction({
        isShowingSlideSearch: false,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variable.scss";

.aha-presentation-editor-wrapper {
  @apply flex flex-col h-full absolute w-full;

  &.show-grid-slide {
    .aha-side-left {
      @apply flex-1;
    }

    .middle-presentation {
      @apply flex-none;
    }
  }
}

.aha-presentation-editor-header.in-demo-mode {
  @apply mt-10;
}

.aha-side-left {
  transition: all .3s ease;
}

@media (max-width: 768px) {
  .editor-mode.aha-middle {
    display: none;
  }

  .aha-side-left {
    max-width: 100%;
    @apply w-full;
  }
}

.slide-search-container {
  background-color: rgba(0, 0, 0, .8); // https://stackoverflow.com/a/14525563
  @apply h-full w-full absolute top-0 left-0 z-10;

  @media (max-width: $ipad-breakpoint) {
    display: none;
  }

  /* if backdrop support: very transparent and blurred */
  // https://stackoverflow.com/a/44366154
  // Fix firefox not support backdrop-filter
  @supports ((-webkit-backdrop-filter: none) or (backdrop-filter: none)) {
    background: rgba(0, 0, 0, 0.4);
    -webkit-backdrop-filter: blur(10px);
    backdrop-filter: blur(10px);
  }
}

.aha-middle {
  &.showing-popup-search {
    &::v-deep .live-demo-audience {
      display: none;
    }
  }
}
</style>
